import { Dispatch, PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { useAppDispatch, useAppSelector } from './hooks';
import { mainActions, sessionIdSelector } from './main-slice';
import { api2, useGetCartQuery, useGetCustomerCartsQuery } from './api-slice';
import { RootState } from './store';
import { updateBasketQuantity, deleteBasket, deleteCart, getBasket, updateCartNameAndDescription, getBasketByCode, addBasketToCart, uploadCartImage, saveCartState, getCustomerCarts, getShoppingCartByCode, printCart, startNewShoppingBasket } from './api-client';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { closePopup, showPopup } from './popup-slice';
import { CartItem, SaveState } from './types/api-types';
import { configuratorActions } from './configurator-slice';
import { TagDescription } from '@reduxjs/toolkit/query';
import { takeSnapshot } from '../main';
import { MAIN_SCENE } from './types/composition';

const IMAGE_WIDTH = 640;
const IMAGE_HEIGHT = 480;

export type BasketModalOrigin = 'home' | 'save-dialog';
export type BasketModalScreen = 'carts' | 'edit-cart';

export interface BasketState {

    // modal
    modalOrigin: BasketModalOrigin
    screen: BasketModalScreen
    modalCart?: number
    nCartOperations: number
    deletingCarts: number[]
    deletingBaskets: number[]
    loadingEditBasket: boolean

    // When adding cart to a product, this saves the ID of the cart
    // while the user navigates freely
    workCartId?: number
    // If editing a previous basket (configuration), this is the ID
    workBasketId?: number

}

const initialState: BasketState = {
    modalOrigin: 'home',
    screen: 'carts',
    nCartOperations: 0,
    deletingCarts: [],
    deletingBaskets: [],
    loadingEditBasket: false
}

const onCloseAsync = createAsyncThunk<void, void, {state: RootState}>(
    'basket/onClose',
    async (_, { dispatch }) => {
        dispatch(closePopup());
        // Trigger to not use cache in next api call
        dispatch(api2.util.invalidateTags([
            { type: 'Carts' },
        ] ));
        dispatch(basketActions.close());
    }
)

const onCartsBackAsync = createAsyncThunk<void, void, {state: RootState}>(
    'basket/onCartsBack',
    async (_, { getState, dispatch }) => {
        const state = getState();
        if(state.basket.modalOrigin === 'home') {
            dispatch(basketActions.onClose());
        } else {
            dispatch(basketActions.goToScreen('edit-cart'));
        }
    }
)

const onEditBackAsync = createAsyncThunk<void, void, {state: RootState}>(
    'basket/onEditBack',
    async (_, { getState, dispatch }) => {
        const state = getState();

        // Trigger to not use cache in next api call
        dispatch(api2.util.invalidateTags([
            { type: 'Carts' },
        ] ));
        
        if(state.basket.modalOrigin === 'save-dialog') {
            dispatch(basketActions.onClose());
        } else {
            dispatch(basketActions.goToScreen('carts'));
        }
    }
)

const addProductToCartAsync = createAsyncThunk<void, void, {state: RootState}>(
    'basket/addProductToCart',
    async (_, { getState, dispatch }) => {
        const state = getState();
        const sessionId = state.main.sessionId;
        if(sessionId) {
            const invalidate: TagDescription<'Carts'|'Cart'>[] = [];
            invalidate.push({ type: 'Carts' });
            try {
                const workCartId = state.basket.workCartId;
                // this should be the previous one (if working on a existing cart)
                // or a new one. It never should be a different one
                const result = await addBasketToCart(workCartId, sessionId);
                if(result) {
                    const cartId = (result.cartId ?? workCartId)!;
                    invalidate.push( { type: 'Cart', id: cartId } );                
                    dispatch( basketActions.setWorkBasketId({ cartId, basketId: result.basketId }) );
                    dispatch( basketActions.openFromSaveConfiguration( cartId ) );
                    dispatch( showPopup({ dialog: 'baskets' }) )    
                } else {
                    throw new Error('Cannot add product to cart');
                }    
            } finally {
                dispatch(api2.util.invalidateTags(invalidate));
            }
            
        } else {
            throw new Error('Cannot add product to cart without session');
        }
    }
)

const deleteBasketsAsync = createAsyncThunk<void, { cartId: number, basketIds: number[] }, {state: RootState}>(
    'basket/deleteBasket',
    async (args, thunkAPI) => {
        const state = thunkAPI.getState();
        const sessionId = state.main.sessionId;
        if(sessionId) {
            thunkAPI.dispatch(basketActions.addDeletingBaskets(args.basketIds));
            for(const basketId of args.basketIds) {
                await deleteBasket(basketId, sessionId);
            }
            thunkAPI.dispatch(api2.util.invalidateTags([
                { type: 'Carts' },
                { type: 'Cart', id: args.cartId }
            ] ));
            thunkAPI.dispatch(basketActions.removeDeletingBaskets(args.basketIds));
        } else {
            throw new Error('Cannot add product to cart without session');
        }
    }
)

const updateCartNameAndDescriptionAsync = createAsyncThunk<void, { name: string, description: string }, {state: RootState}>(
    'basket/updateCartName',
    async (args, thunkAPI) => {
        const state = thunkAPI.getState();
        const sessionId = state.main.sessionId;
        const cartId = state.basket.modalCart;
        if(sessionId && cartId) {
            const cartSelector = api2.endpoints.getCart.select({cartId, sessionId});
            const cartResult = cartSelector(state);
            if(cartResult.isSuccess) {
                const response: any = await updateCartNameAndDescription(cartId, args.name, args.description, sessionId);
                if(response.SUCCESS) {
                    thunkAPI.dispatch(api2.util.invalidateTags([
                        { type: 'Carts' },
                        { type: 'Cart', id: cartId }
                    ] ));
                } else {
                    throw new Error('Failed to update cart name');
                }    
            } else {
                throw new Error('Failed to update cart name');
            }
        } else {
            throw new Error('Cannot change article quantity without session');
        }
    }
)

const updateBasketQuantityAsync = createAsyncThunk<void, { cartId: number, basketId: number, quantity: number }, {state: RootState}>(
    'basket/updateBasketQuantityAsync',
    async (args, thunkAPI) => {
        const state = thunkAPI.getState();
        const sessionId = state.main.sessionId;
        if(sessionId) {
            const response: any = await updateBasketQuantity(args.cartId, args.basketId, args.quantity, sessionId);
            if(response.SUCCESS) {
                thunkAPI.dispatch(api2.util.invalidateTags([
                    { type: 'Carts' },
                    { type: 'Cart', id: args.cartId }
                ] ));
            } else {
                throw new Error('Failed to update cart reference');
            }
        } else {
            throw new Error('Cannot change article quantity without session');
        }
    }
)

const deleteCartAsync = createAsyncThunk<void, number, {state: RootState}>(
    'basket/deleteCartAsync',
    async (cartId, thunkAPI) => {
        const state = thunkAPI.getState();
        const sessionId = state.main.sessionId;
        if(sessionId) {
            thunkAPI.dispatch(basketActions.addDeletingCart(cartId));
            await deleteCart(cartId, sessionId);
            thunkAPI.dispatch(api2.util.invalidateTags([
                { type: 'Carts' }
            ] ));
            thunkAPI.dispatch(basketActions.removeDeletingCart(cartId));            
        } else {
            throw new Error('Cannot delete without session');
        }
    }
)

const printShoppingBasketAsync = createAsyncThunk<string | undefined, number, { state: RootState }>(
    'basket/printShoppingBasket',
    async (cartId, thunkAPI) => {
        const state = thunkAPI.getState();
        const sessionId = state.main.sessionId;
        if (!sessionId) return undefined; 

        const pdfResponse = await printCart(cartId, sessionId);
        const pdfURL = (pdfResponse.SUCCESS && pdfResponse.pdf) ? pdfResponse.pdf : undefined;

        return pdfURL;
    }
);


const save3DSnapshotAsync = createAsyncThunk<void, void, {state: RootState}>(
    'basket/save3DSnapshot',
    async (_, thunkAPI) => {
        const state = thunkAPI.getState();
        const cartId = state.basket.modalCart;
        const sessionId = state.main.sessionId;
        if(cartId && sessionId) {            
            const blob = await takeSnapshot(MAIN_SCENE, true, IMAGE_WIDTH, IMAGE_HEIGHT);
            await uploadCartImage(blob, cartId, sessionId);
            await saveCartState(cartId, {
                layout: state.basket3D.layout
            }, sessionId);
        }
    }
)

const startAddProductAsync = createAsyncThunk<void, number, {state: RootState}>(
    'basket/startAddProduct',
    async (cartId, thunkAPI) => {
        const state = thunkAPI.getState();
        const sessionId = state.main.sessionId;

        if(cartId && sessionId) {
            await startNewShoppingBasket(cartId, sessionId);
        }

        thunkAPI.dispatch(basketActions.setWorkCartId(cartId));
        thunkAPI.dispatch(basketActions.close());
        thunkAPI.dispatch(closePopup());
    }
)


const startNewConfigurationAsync = createAsyncThunk<void, void, {state: RootState}>(
    'basket/startNewConfiguration',
    async (_, thunkAPI) => {
        thunkAPI.dispatch(basketActions.clearWorkCartId());
        thunkAPI.dispatch(basketActions.close());
        thunkAPI.dispatch(closePopup());    
        thunkAPI.dispatch(api2.util.invalidateTags([ { type: 'Carts' } ] ));
    }
)

const editCart = (cartId: number) => {
    return (dispatch: Dispatch, getState: () => RootState) => {
        dispatch(api2.util.invalidateTags([ { type: 'Cart', id: cartId } ]));
        dispatch(basketActions._editCart(cartId))
    }
    
}

const editCartByCode = ({ cartCode } : { cartCode: string}) => {
    return async (dispatch: Dispatch, getState: () => RootState) => {
        const state = getState();
        const sessionId = state.main.sessionId;
        if(!sessionId) {
            return
        }
        const cart = await getShoppingCartByCode(cartCode, sessionId);
        const cartId = cart.shoppingcartID;
        dispatch(api2.util.invalidateTags([ { type: 'Cart', id: cartId } ]));
        dispatch(basketActions._editCart(cartId))
    }
    
}

// Sync with next function
const editBasketAsync = createAsyncThunk<SaveState, { cartId: number, basketId: number }, {state: RootState}>(
    'basket/editBasket',
    async (args, thunkAPI) => {
        const { cartId, basketId } = args;
        const state = thunkAPI.getState();
        const sessionId = state.main.sessionId;
        if(sessionId) {
            const response = await getBasket(basketId, sessionId);
            if(response.SUCCESS && typeof response.state === 'string' && response.state.trim() !== '') {
                thunkAPI.dispatch(basketActions.setWorkBasketId({cartId,basketId}));
                thunkAPI.dispatch(closePopup());
                thunkAPI.dispatch(basketActions.close());
                const savedState = JSON.parse(response.state) as SaveState;
                thunkAPI.dispatch(configuratorActions.setLoadedContent({
                    groups: savedState.groups
                }));
                return savedState;
            } else {
                throw new Error('Couldn\'t read basket');
            }
        } else {
            throw new Error('Cannot edit without session');
        }
        
    }
)

// Sync with previous function
const editBasketByCodeAsync = createAsyncThunk<SaveState, { code: string }, {state: RootState}>(
    'basket/editBasketByCode',
    async (args, thunkAPI) => {
        const { code } = args;
        const state = thunkAPI.getState();
        const sessionId = state.main.sessionId;
        if(sessionId) {
            const response = await getBasketByCode(code, sessionId);
            if(response.SUCCESS && typeof response.state === 'string' && response.state.trim() !== '') {
                //thunkAPI.dispatch(mainActions.setWorkBasketId({cartId,basketId}));
                thunkAPI.dispatch(closePopup());
                thunkAPI.dispatch(basketActions.close());
                const savedState = JSON.parse(response.state) as SaveState;
                thunkAPI.dispatch(configuratorActions.setLoadedContent({
                    groups: savedState.groups
                }));
                return savedState;
            } else {
                throw new Error('Couldn\'t read code');
            }
        } else {
            throw new Error('Cannot edit without session');
        }
        
    }
)


export const basketSlice = createSlice({
    name: 'basket',
    initialState,
    reducers: {
        openFromHome(state) {
            state.modalOrigin = 'home';
            state.screen = 'carts';
            state.workBasketId = undefined;
            state.workCartId = undefined;
            state.modalCart = undefined;
        },
        openFromSaveConfiguration(state, action: PayloadAction<number>) {
            state.modalOrigin = 'save-dialog';
            state.screen = 'edit-cart';
            state.modalCart = action.payload;
        },
        onBasketsOverview(state) {
            state.screen = 'carts';
            if(state.modalOrigin === 'home') {
                state.modalCart = undefined;    
            }
            state.workCartId = undefined;
            state.workBasketId = undefined;
        },
        goToScreen(state, action: PayloadAction<BasketModalScreen>) {
            state.screen = action.payload;
            if(action.payload === 'carts') {
                state.workBasketId = undefined;
                state.workCartId = undefined;
            }
        },
        _editCart(state, action: PayloadAction<number>) {
            state.modalCart = action.payload;
            state.screen = 'edit-cart';
        },
        close(state) {
            state.modalCart = undefined;
            state.screen = 'carts';
            state.modalOrigin = 'home';
        },        
        addDeletingCart(state, action: PayloadAction<number>) {
            state.deletingCarts.push(action.payload);
        },
        removeDeletingCart(state, action: PayloadAction<number>) {
            state.deletingCarts = state.deletingCarts.filter(dc => dc !== action.payload);
        },
        addDeletingBaskets(state, action: PayloadAction<number[]>) {
            state.deletingBaskets.push(...action.payload);
        },
        removeDeletingBaskets(state, action: PayloadAction<number[]>) {
            state.deletingBaskets = state.deletingBaskets.filter(db => !action.payload.includes(db));
        },
        setWorkCartId: (state, action: PayloadAction<number>) => {
            state.workCartId = action.payload;
            state.workBasketId = undefined;
        },
        clearWorkCartId: (state) => {
            state.workCartId = undefined;
            state.workBasketId = undefined;
        },
        setWorkBasketId: (state, action: PayloadAction<{ cartId: number, basketId: number }>) => {
            state.workCartId = action.payload.cartId;
            state.workBasketId = action.payload.basketId;
        },


    },
    extraReducers: (builder) => {

        builder.addCase(mainActions.enterPage, (state, action) => {
            if(action.payload !== 'configurator') {
                state.workBasketId = undefined;
            }
        })
        
        .addCase(addProductToCartAsync.pending, (state, action) => {
            state.nCartOperations += 1;
        })
        .addCase(addProductToCartAsync.fulfilled, (state, action) => {
            state.nCartOperations -= 1;
        })
        .addCase(addProductToCartAsync.rejected, (state, action) => {
            state.nCartOperations -= 1;
        })

        .addCase(updateBasketQuantityAsync.pending, (state, action) => {
            state.nCartOperations += 1;
        })
        .addCase(updateBasketQuantityAsync.fulfilled, (state, action) => {
            state.nCartOperations -= 1;
        })
        .addCase(updateBasketQuantityAsync.rejected, (state, action) => {
            state.nCartOperations -= 1;
        })

        .addCase(editBasketAsync.pending, (state, action) => {
            state.loadingEditBasket = true;
        })
        .addCase(editBasketAsync.fulfilled, (state, action) => {
            state.loadingEditBasket = false;
        })
        .addCase(editBasketAsync.rejected, (state, action) => {
            state.loadingEditBasket = false;
        })

    }
});

export default basketSlice.reducer;

export const basketActions = {
    ...basketSlice.actions, 
    editCart,
    editCartByCode,
    onCartsBack: onCartsBackAsync,
    onClose: onCloseAsync,
    onEditBack: onEditBackAsync,
    addProductToCart: addProductToCartAsync, 
    updateBasketQuantity: updateBasketQuantityAsync,
    deleteCart: deleteCartAsync,
    updateCartNameAndDescription: updateCartNameAndDescriptionAsync,
    startAddProduct: startAddProductAsync,
    editBasket: editBasketAsync,
    editBasketByCode: editBasketByCodeAsync,
    deleteBaskets: deleteBasketsAsync,
    startNewConfiguration: startNewConfigurationAsync,
    save3DSnapshot: save3DSnapshotAsync,
    printShoppingBasket: printShoppingBasketAsync
};

export const selectBasketScreen = (state: RootState) => state.basket.screen; 
export const selectCurrentCart = (state: RootState) => state.basket.modalCart;
export const selectDeletingCarts = (state: RootState) => state.basket.deletingCarts;
export const selectDeletingBaskets = (state: RootState) => state.basket.deletingBaskets;
export const selectLoadingEditBasket = (state: RootState) => state.basket.loadingEditBasket;
export const selectWorkBasket = (state: RootState) => state.basket.workBasketId;
export const selectWorkCart = (state: RootState) => state.basket.workCartId;


export function useCurrentCarts() {
    const sessionId = useAppSelector(sessionIdSelector);
    return useGetCustomerCartsQuery(sessionId!, { skip: sessionId === undefined });
}

/*
export function useCartDataFromCartsList(): [ boolean, string|undefined, string|undefined, number|undefined, string|undefined ] {
    const currentCartId = useAppSelector(selectCurrentCart);
    const { data, isSuccess } = useCurrentCarts();
    if(isSuccess) {
        const cartItem = data.find(ci => ci.shoppingcartID === currentCartId);
        if(cartItem) {
            return [ true, cartItem.name, cartItem.description, cartItem.amount, cartItem.currencycode ];
        } else {
            return [ false, undefined, undefined, undefined, undefined ];
        }
    } else {
        return [ false, undefined, undefined, undefined, undefined ];
    }

}
*/

export function useCurrentCart() {
    const cartId = useAppSelector(selectCurrentCart);
    const sessionId = useAppSelector(sessionIdSelector);
    return useGetCartQuery({
        cartId: cartId!,
        sessionId: sessionId!
    }, { skip: cartId === undefined || sessionId === undefined });
}

export function useReloadCarts() {
    const sessionId = useSelector(sessionIdSelector);
    const dispatch = useAppDispatch();
    const reloadCarts = useCallback(() => {
        if(sessionId) {
            dispatch(api2.endpoints.getCustomerCarts.initiate(sessionId))
        }
    }, [ sessionId, dispatch ])
    return reloadCarts;
}